/**
 * gnbWrapper.tsx
 * [Readme] --------------------------------------------------------------------------------------
 *
 * 데스크탑 GNB 헤더 영역 렌더링 컴포넌트
 *
 * -----------------------------------------------------------------------------------------------
 */

import bg from 'assets/Desktop/images/common/bg_bla_gra.png';
import { HideText } from 'common-modules/general/assets/Styles/Common.style';
import HeaderModel, { GNBSubMenu } from 'common-modules/general/Common/Logic/Header/HeaderCommon';
import usePrevious from 'common-modules/general/Modules/custom-hooks/usePrevious';
import { RoutesString } from 'common-modules/general/Config/routesString';
import { cloneDeep } from 'lodash';
import { toJS } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

interface StyleType {
  block?: Boolean;
  color?: any;
}

const DimmImage = styled.img<{ disabled: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  height: 323px;
  height: 20.1875rem;
  width: 100%;
  pointer-events: none;

  ${props =>
    props.disabled &&
    css`
      visibility: hidden;
      pointer-events: none;
    `};
`;

const GnbMenuWrapper = styled.div`
  width: 100%;
`;

const GnbMenuItem = styled.li`
  position: relative;
  display: inline-table;
  color: #666;
  width: 15rem;
  cursor: pointer;

  &:hover {
    span {
      transition: color 0.3s ease, border 0.3s ease, background-color 0.3s;
      color: #ff671d !important;
    }
  }
`;

const GnbSubMenuWrapper = styled.div`
  visibility: hidden;
  display: block;
  width: 100%;
  background-color: #fff;
  box-shadow: 0 10px 10px rgb(0 0 0 / 9%);
  position: absolute;
  left: 0;
  top: 84px;

  &.temporarily-enable-non-access {
    visibility: visible;
    pointer-events: all;
    display: none;
  }
`;

const GnbSectionWrapper = styled.div`
  position: relative;
  width: 100%;
  min-height: 5.25rem;

  &:hover {
    background-color: #fff;
    border: none;

    ${DimmImage} {
      display: none;
    }

    ${GnbSubMenuWrapper} {
      display: block;
    }

    ${GnbMenuItem} {
      span {
        color: #666;
      }
    }
  }
`;

/*
  상황 root. 선택 됐을때 / hover 됐을 때 orange

  상황 1-1. Main 페이지일 때
    - color white
  상황 1-2. Main 페이지 아닐 때
    - color 666 
  상황 2. Fixed 될 때
    - color 666
  상황 3. Hover 될 때
    - color 666
*/

const GnbMenuList = styled.ul<{ sticky: boolean; isDisabledActive: boolean }>`
  width: 100%;
  text-align: center;
  display: table;
  padding-top: 20px;

  ${GnbMenuItem} {
    span {
      ${({ isDisabledActive }) =>
        isDisabledActive &&
        css`
          color: #fff;
        `};

      ${({ sticky }) =>
        sticky &&
        css`
          color: #666;
        `};
    }
  }
`;

const GNBSubBg = styled.div`
  display: none;
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 100vh;
  border: 1px solid #eee;
  border-top: 0;
  border-bottom: 0;
  background: #f7f7f7;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
`;

const GnbMenuLink = styled.span`
  display: block;

  padding: 0.625rem 0 0 0;

  transition: width 0.3s ease-out;
`;

const MenuText = styled.span`
  color: ${(props: StyleType) => props.color};
  font-size: 1.25rem;
  font-family: 'SDGothicNeobTTF-dMd', 'sans-serif';
  z-index: 1;
  position: relative;

  &.on {
    color: #ff671d !important;
    padding-bottom: 28px;
    display: inline-block;
    &.main {
      border-bottom: 2px solid #ff671d;
    }
  }
`;

const SubMenuArea = styled.div`
  width: 100%;
  z-index: 10;
  text-align: center;
  display: flex;
  align-items: center;
`;

const SubMenuBox = styled.div`
  display: inline-table;
  vertical-align: top;
  overflow: hidden;
  padding-top: 84px;
  margin-top: -82px;
  margin-left: auto;
  margin-right: auto;
`;

const SubMenuList = styled.div`
  position: relative;
  display: table-cell;
  vertical-align: top;
  padding-bottom: 40px;
  &:hover {
    ${GNBSubBg} {
      display: block;
    }
  }
`;

const SubMenuItem = styled.div`
  position: relative;
  z-index: 1;
  display: block;
`;

const LinkA = styled(Link)<{ highlight?: boolean }>`
  display: block;
  padding: 0.625rem 1.5625rem 0.625rem 1.5625rem;
  &:hover,
  &:focus,
  &:active {
    span {
      transition: color 0.3s ease, border 0.3s ease, background-color 0.3s;
      color: #ff671d;
      text-decoration: underline;
    }
  }

  ${props =>
    props.highlight &&
    css`
      span {
        transition: color 0.3s ease, border 0.3s ease, background-color 0.3s;
        color: #ff671d;
        text-decoration: underline;
      }
    `}
`;
const Text = styled.span`
  font-size: 1rem;
  color: #666;
  font-family: 'SDGothicNeobTTF-bLt', 'sans-serif';
`;

const Href = styled.a<{ highlight?: boolean }>`
  display: block;
  padding: 0.625rem 1.5625rem 0.625rem 1.5625rem;
  &:hover,
  &:focus,
  &:active {
    span {
      transition: color 0.3s ease, border 0.3s ease, background-color 0.3s;
      color: #ff671d;
      text-decoration: underline;
    }
  }

  ${props =>
    props.highlight &&
    css`
      span {
        transition: color 0.3s ease, border 0.3s ease, background-color 0.3s;
        color: #ff671d;
        text-decoration: underline;
      }
    `}
`;

export const GnbWrapper = observer((props: any) => {
  const location = useLocation();
  const { model: mainModel } = useLocalObservable(() => ({ model: new HeaderModel() }));

  const mainMenuContainerRef = useRef<HTMLUListElement>(null);
  const subMenuContainerRef = useRef<HTMLDivElement>(null);
  const previousMainData = usePrevious(mainModel.mainData.data);

  const { subMenuList } = toJS(mainModel.getMenuList());

  useEffect(() => {
    mainModel.getData();
  }, []);

  const getSelectedMenu = (menuList: Array<GNBSubMenu>): GNBSubMenu | null => {
    const { pathname } = location;
    const sorted = cloneDeep(menuList).sort((a, b) => b.urlBizLndLnk.split('/').length - a.urlBizLndLnk.split('/').length);
    let findItem;

    if (props.isMain) {
      findItem = sorted.find(data => pathname.includes(data.urlBizLndLnk));
    } else {
      findItem = sorted.find(data => pathname.includes(data.cdBizLnd));
    }

    if (findItem) {
      return findItem;
    } else {
      return null;
    }
  };

  const menuRender = () => {
    return subMenuList.map((item, index) => {
      const selectedChildrenMenu = getSelectedMenu(item.menuList);
      return (
        <GnbMenuItem className="gnb-menu-item" key={`${item.name}_${index}`}>
          {/* <!-- 20200409 a태그를 span 태그로 변경--> */}
          <GnbMenuLink>
            <HideText>선택됨</HideText>
            <MenuText
              // onClick={() => (window.location.href = subMenuList[index].menuList[0].urlBizLndLnk)}
              className={`${selectedChildrenMenu ? 'on' : ''} ${props.isMain ? 'main' : ''}`}>
              {item.name}
            </MenuText>
          </GnbMenuLink>
        </GnbMenuItem>
      );
    });
  };

  const subMenuRender = () => {
    return (
      subMenuList &&
      subMenuList.map((item, key) => {
        const selectedMenu = getSelectedMenu(item.menuList);
        const isMain = process.env.REACT_APP_IS_BRAND;

        return (
          <SubMenuList key={`${item.name}_${key}`}>
            <GNBSubBg />
            {item.menuList.map((val, index) => {
              //  return (
              //   <SubMenuItem key={`${val}_${index}`} aria-hidden="true" className="gnb-sub-item">
              //     <Href highlight={selectedMenu?.urlBizLndLnk === val.urlBizLndLnk} href={val.urlBizLndLnk}>
              //       <HideText>선택됨</HideText>
              //       <Text>{val.nmBizLnd}</Text>
              //     </Href>
              //   </SubMenuItem>
              // );
              if (!isMain || item.name == '분양' && index != 0) { // 분양사이트&브랜드 연간분양일정 = href
                return (
                  <SubMenuItem key={`${val}_${index}`} aria-hidden="true" className="gnb-sub-item">
                    <Href highlight={selectedMenu?.urlBizLndLnk === val.urlBizLndLnk} href={val.urlBizLndLnk}>
                      <HideText>선택됨</HideText>
                      <Text>{val.nmBizLnd}</Text>
                    </Href>
                  </SubMenuItem>
                );
              } else {
                return (
                  <SubMenuItem key={`${val}_${index}`} aria-hidden="true" className="gnb-sub-item">
                    <LinkA
                      highlight={selectedMenu?.urlBizLndLnk === val.urlBizLndLnk}
                      to={val.urlBizLndLnk}
                      onClick={() => closeMenu()}>
                      <HideText>선택됨</HideText>
                      <Text>{val.nmBizLnd}</Text>
                    </LinkA>
                  </SubMenuItem>
                );
              }
            })}
          </SubMenuList>
        );
      })
    );
  };

  const verifyBeforeApplyingCSSProperties = (
    targetElement: HTMLElement,
    cssPropertiesKey: keyof CSSStyleDeclaration,
    value: any
  ): boolean => {
    if (targetElement[cssPropertiesKey] === value) return false;
    else return true;
  };

  const applySameSizeAllMenuElement = (width: number) => {
    if (subMenuContainerRef.current && mainMenuContainerRef.current) {
      const widthConvertedToPx = `${width}px`;

      const { children: mainMenuChild } = mainMenuContainerRef.current;
      const { children: subMenuChild } = subMenuContainerRef.current;

      for (let i = 0; i < mainMenuChild.length; i++) {
        const subMenuElement: HTMLElement = mainMenuChild[i] as HTMLElement;
        const mainMenuElement: HTMLElement = subMenuChild[i] as HTMLElement;

        if (subMenuElement && mainMenuElement) {
          if (verifyBeforeApplyingCSSProperties(subMenuElement, 'width', widthConvertedToPx)) {
            subMenuElement.style.width = widthConvertedToPx;
          }

          if (verifyBeforeApplyingCSSProperties(mainMenuElement, 'width', widthConvertedToPx)) {
            mainMenuElement.style.width = widthConvertedToPx;
          }
        } else {
          continue;
        }
      }
    }
  };

  const removeTemporarilyEnableParentElement = () => {
    const parentClassNameElementList = document.getElementsByClassName('header-sub-block');

    if (parentClassNameElementList.length) {
      const [parentElement] = parentClassNameElementList;

      parentElement.classList.add('temporarily-enable-non-access');
    }
  };

  const closeMenu = () => {
    const parentClassNameElementList = document.getElementsByClassName('header-sub-block');
    if (parentClassNameElementList.length) {
      const subHeader = parentClassNameElementList[0] as HTMLElement;
      subHeader.style.display = 'none';
    }
    document.getElementById('HeaderWrapper')?.classList.remove('disabled-active');
    
    const gnbWrap = document.getElementById('gnbWrap') as HTMLElement;
    gnbWrap.style.backgroundColor = '#fff';
    gnbWrap.style.border = 'none';
    
    document.querySelectorAll('.gnb-menu-item span').forEach((item)=>{
      (item as HTMLElement).style.color = 'rgb(102, 102, 102)';
    })
  };

  /**
   * GNB 분양 페이지의 분양지 리스트 글자를 추출하고 그 중 가장 긴 글자를 선택해 해당 기준으로 영역 사이즈를 조정
   */
  const unifyingSizeRootMenuWithSubMenu = () => {
    if (subMenuContainerRef.current?.children) {
      let maxContentWidth = 200;

      for (let i = 0; i < subMenuContainerRef.current.children.length; i++) {
        const searchItem = subMenuList[i].menuList.sort((a, b) => {
          if (a.nmBizLnd.length > b.nmBizLnd.length) {
            return -1;
          }
          return 1;
        })[0];
        const widthConvertedToNumber = searchItem.nmBizLnd.length * 16;
        if (maxContentWidth < widthConvertedToNumber) {
          maxContentWidth = widthConvertedToNumber;
        } else {
          continue;
        }
      }

      removeTemporarilyEnableParentElement();
      applySameSizeAllMenuElement(maxContentWidth);
    }
  };

  useEffect(() => {
    if (mainModel.mainData.data && !previousMainData) {
      unifyingSizeRootMenuWithSubMenu();
    }
  });

  const { pathname } = useLocation();

  return (
    <GnbSectionWrapper id="gnbWrap" className={'gnb-wrap'}>
      <DimmImage disabled={props.isMain && pathname !== RoutesString.Main} className="dimm-image" src={bg} />

      <GnbMenuWrapper>
        <GnbMenuList ref={mainMenuContainerRef} isDisabledActive sticky={props.sticky} className="gnb-1depth-list">
          {menuRender()}
        </GnbMenuList>
      </GnbMenuWrapper>
      <GnbSubMenuWrapper className="header-sub-block">
        <SubMenuArea>
          <SubMenuBox ref={subMenuContainerRef} className="">
            {subMenuRender()}
          </SubMenuBox>
        </SubMenuArea>
      </GnbSubMenuWrapper>
    </GnbSectionWrapper>
  );
});
